import React, { useState } from 'react';
import { signInWithEmail, signUpWithEmail, signInWithGoogle } from '../services/auth.js';

export default function Login({ onLoginSuccess }) {
    const [isSignUp, setIsSignUp] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [referrerId, setReferrerId] = useState(null);

    // Capture Referral Code
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) {
            setReferrerId(ref);
            localStorage.setItem('spark_referrer_id', ref); // Persist
            console.log("Referral code captured:", ref);
        } else {
            // Check storage if not in URL
            const stored = localStorage.getItem('spark_referrer_id');
            if (stored) setReferrerId(stored);
        }
    }, []);

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (isSignUp) {
                if (!displayName.trim()) {
                    setError('Please enter a display name');
                    setLoading(false);
                    return;
                }
                await signUpWithEmail(email, password, displayName, referrerId);
            } else {
                await signInWithEmail(email, password);
            }
            onLoginSuccess();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setLoading(true);

        try {
            await signInWithGoogle(referrerId);
            onLoginSuccess();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    return React.createElement('div', {
        style: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-dark)',
            padding: '20px'
        }
    },
        React.createElement('div', {
            className: 'glass-panel animate-fade-in',
            style: {
                maxWidth: '400px',
                width: '100%',
                padding: '40px',
                textAlign: 'center'
            }
        },
            // Logo/Title
            React.createElement('div', { style: { marginBottom: '30px' } },
                React.createElement('h1', {
                    style: {
                        fontSize: '32px',
                        background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        marginBottom: '8px'
                    }
                }, '✨ Spark Inspo'),
                React.createElement('p', { style: { color: '#999', fontSize: '14px' } },
                    'Transform boredom into creative inspiration'
                )
            ),

            // Error message
            error && React.createElement('div', {
                style: {
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid #ef4444',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '20px',
                    color: '#ef4444',
                    fontSize: '14px'
                }
            }, error),

            // Google Sign In
            React.createElement('button', {
                onClick: handleGoogleSignIn,
                disabled: loading,
                className: 'btn',
                style: {
                    width: '100%',
                    marginBottom: '20px',
                    background: 'white',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    opacity: loading ? 0.6 : 1
                }
            },
                React.createElement('svg', { width: '20', height: '20', viewBox: '0 0 24 24' },
                    React.createElement('path', {
                        fill: '#4285F4',
                        d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                    }),
                    React.createElement('path', {
                        fill: '#34A853',
                        d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                    }),
                    React.createElement('path', {
                        fill: '#FBBC05',
                        d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                    }),
                    React.createElement('path', {
                        fill: '#EA4335',
                        d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                    })
                ),
                'Continue with Google'
            ),

            // Divider
            React.createElement('div', {
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    margin: '20px 0',
                    gap: '10px'
                }
            },
                React.createElement('div', { style: { flex: 1, height: '1px', background: 'var(--border)' } }),
                React.createElement('span', { style: { color: '#999', fontSize: '12px' } }, 'OR'),
                React.createElement('div', { style: { flex: 1, height: '1px', background: 'var(--border)' } })
            ),

            // Email/Password Form
            React.createElement('form', { onSubmit: handleEmailAuth },
                isSignUp && React.createElement('input', {
                    type: 'text',
                    placeholder: 'Display Name',
                    value: displayName,
                    onChange: (e) => setDisplayName(e.target.value),
                    required: isSignUp,
                    style: {
                        width: '100%',
                        padding: '12px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'var(--text-main)',
                        fontSize: '14px'
                    }
                }),

                React.createElement('input', {
                    type: 'email',
                    placeholder: 'Email',
                    value: email,
                    onChange: (e) => setEmail(e.target.value),
                    required: true,
                    style: {
                        width: '100%',
                        padding: '12px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'var(--text-main)',
                        fontSize: '14px'
                    }
                }),

                React.createElement('input', {
                    type: 'password',
                    placeholder: 'Password (min 6 characters)',
                    value: password,
                    onChange: (e) => setPassword(e.target.value),
                    required: true,
                    minLength: 6,
                    style: {
                        width: '100%',
                        padding: '12px',
                        marginBottom: '20px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'var(--text-main)',
                        fontSize: '14px'
                    }
                }),

                React.createElement('button', {
                    type: 'submit',
                    disabled: loading,
                    className: 'btn',
                    style: {
                        width: '100%',
                        opacity: loading ? 0.6 : 1
                    }
                }, loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In'))
            ),

            // Toggle Sign Up/Sign In
            React.createElement('p', {
                style: {
                    marginTop: '20px',
                    color: '#999',
                    fontSize: '14px'
                }
            },
                isSignUp ? 'Already have an account? ' : "Don't have an account? ",
                React.createElement('button', {
                    onClick: () => {
                        setIsSignUp(!isSignUp);
                        setError('');
                    },
                    style: {
                        background: 'none',
                        border: 'none',
                        color: 'var(--primary)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '14px'
                    }
                }, isSignUp ? 'Sign In' : 'Sign Up')
            )
        )
    );
}
